import React, { useState } from "react"

import { NotificationManager } from "react-notifications"
import { gql, useMutation, useQuery } from "urql"

import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline"

import { Color } from "~tailwindui/types/enums"

import {
  AlertMessageBox,
  Button,
  WithLoadingIndicator,
} from "~tailwindui/Basics"

import classNames from "src/classNames"
import { RunMutation } from "src/types"

import DegreeFormModal from "./DegreeFormModal"

export type EducationHistoryProps = Record<never, any>

const EducationHistory: React.FC<EducationHistoryProps> = () => {
  const [degreeFormModalOpen, setDegreeFormModalOpen] = useState(false)
  const [degreeError, setDegreeError] = useState<string>()

  const [result, refetchQuery] = useQuery({ query })
  const [, runRemoveDegreeMutation]: [any, RunMutation] =
    useMutation(removeDegreeMutation)

  const removeDegree = id => {
    runRemoveDegreeMutation({ id })
      .then(
        response => {
          const { failures } = response.data.removeDegree
          if (failures.length > 0) {
            setDegreeError(failures[0].message)
          } else {
            refetchQuery({ requestPolicy: "network-only" })
            NotificationManager.success("Degree removed from profile")
          }
        },
        () => setDegreeError("Something went wrong")
      )
      .catch(() => setDegreeError("Something went wrong"))
  }

  return (
    <>
      <WithLoadingIndicator result={result}>
        {({ data: { viewer } }) => (
          <>
            <span className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
              Degrees
            </span>
            <ul>
              {viewer.degrees.map(degree => (
                <li
                  key={degree.id}
                  className="mb-4 flex flex-col justify-between space-y-2 rounded-md bg-gray-100 p-4 text-gray-700 sm:flex-row sm:items-center sm:space-y-0"
                >
                  <div className="mr-2 font-medium">
                    <div>
                      {degree.degreeType} - {degree.degree}
                    </div>
                    <div className="text-sm">
                      {degree.year}, {degree.college}, {degree.country}
                    </div>
                  </div>
                  <div className="flex space-x-4">
                    <div
                      className={classNames(
                        "pt-2 font-semibold uppercase",
                        degree.approvalState === "approved"
                          ? "text-emerald-500"
                          : degree.approvalState === "rejected"
                          ? "text-red-500"
                          : "text-gray-500"
                      )}
                    >
                      {degree.approvalState}
                    </div>
                    <Button
                      soft
                      color={Color.Red}
                      onClick={() => removeDegree(degree.id)}
                    >
                      Remove
                      <TrashIcon className="ml-2 h-5 w-5 sm:mt-0" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mb-6">
              <Button soft onClick={() => setDegreeFormModalOpen(true)}>
                Add Degree
                <PlusIcon className="ml-2 inline-block h-5 w-5 text-sky-600" />
              </Button>
              {degreeFormModalOpen && (
                <DegreeFormModal
                  closeForm={() => setDegreeFormModalOpen(false)}
                  refetchQuery={refetchQuery}
                />
              )}

              {degreeError && <AlertMessageBox>{degreeError}</AlertMessageBox>}
            </div>
          </>
        )}
      </WithLoadingIndicator>
    </>
  )
}

const query = gql`
  query {
    viewer {
      id
      degrees {
        id
        college
        year
        country
        degree
        degreeType
        level
        levelOther
        approvalState
        transcript {
          filename
          downloadUrl
        }
      }
    }
  }
`

const removeDegreeMutation = gql`
  mutation ($id: ID!) {
    removeDegree(id: $id) {
      failures {
        message
      }
    }
  }
`

export default EducationHistory
